<template>
  <VanPopup
    :show="props.show"
    @update:show="emit('update:show', $event)"
    class="p-4 pt-5 flex flex-col max-h-[80%]"
    position="bottom"
    closeable
  >
    <div>
      <div class="font-bold mb-1">任务目标</div>
      <VanField v-model="tmpObjective" type="textarea" autosize />
    </div>

    <div class="flex items-center gap-2 justify-between mt-5">
      <div class="font-bold mb-2">
        <span>任务难度:</span>
        <span :class="[`difficulty-level${tmpDifficultyLevel}`]">
          {{
            tmpDifficultyLevel
              ? $t(`mission.difficultyLevel.${tmpDifficultyLevel}`)
              : $t(`Message.none`)
          }}
        </span>
      </div>
      <div>
        <button class="btn btn-xs" @click="clickSelectDifficultyLevel">
          修改难度
        </button>
      </div>
    </div>
    <div v-if="tmpReason">
      <div class="font-semibold mb-2">原因：</div>
      <div class="font-bold">
        {{ tmpReason }}
      </div>
    </div>
    <!--  前情提要 -->
    <MissionJourneySelect
      v-if="dispatchMission.mateId"
      :dispatch-mission="dispatchMission"
      :is-edit="true"
      extra-class="mt-5"
      :leader-mate="leaderMate!"
    />

    <div class="flex gap-2 mt-2">
      <button class="btn btn-xs flex-1" @click="clickSave">
        {{ $t(`Buttons.save`) }}
      </button>
      <button class="btn btn-xs btn-primary" @click="clickGenerateObjective">
        {{
          tmpObjective
            ? tmpObjective === returnedObjective
              ? "重新生成目标"
              : "评定难度"
            : "生成目标"
        }}
        <template v-if="!userStore.isFreeModel()">
          <PowerIcon />
          {{ userStore.getPowerCostByModel(1) }}
        </template>
      </button>
      <SelectModelBtn />
    </div>
  </VanPopup>

  <ModalSelectOption
    v-model:show="showDifficultyLevelModal"
    :options="difficultyLevelOptions"
    @confirm="clickDifficultyLevel"
  />
</template>

<script setup lang="ts">
import { ApiPostGenerateObjective } from "@/apis/dispatchMission";
import { DispatchDifficultyLevel } from "@/enums/mission";
import { useGlobalModal } from "@/stores/globalModal";
import { Mate, MateDispatch } from "@/types/entity";
import { showConfirmDialog, showLoadingToast } from "vant";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useGlobalUser } from "@/stores/globalUser";

const props = defineProps<{
  show: boolean;
  dispatchMission: MateDispatch & { objectiveSignature: string };
  leaderMate?: Mate;
}>();

const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:show", value: boolean): void;
}>();

const tmpObjective = ref();

const returnedObjective = ref();

const tmpDifficultyLevel = ref<DispatchDifficultyLevel | undefined>();

const tmpReason = ref();

watch(
  () => [props.show, props.dispatchMission.objective],
  ([val]) => {
    if (val) {
      tmpObjective.value = props.dispatchMission.objective || "";
      tmpDifficultyLevel.value = props.dispatchMission.difficultyLevel;
      tmpReason.value = props.dispatchMission.difficultyLevelReason || "";
      returnedObjective.value = props.dispatchMission.objective || "";
    }
  }
);

const tmpSignature = ref(props.dispatchMission.objectiveSignature || "");

const modalStore = useGlobalModal();
const userStore = useGlobalUser();
const clickGenerateObjective = async () => {
  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: 1,
      cb: clickGenerateObjective,
      plusModelRatio: true,
    })
  )
    return;

  const { dispatchMission } = props;
  const {
    mateId,
    mission,
    location,
    dispatchMemberzz,
    carriedJournalzz,
    carriedMailzz,
    carriedDispatchzz,
  } = dispatchMission;

  if (tmpObjective.value === returnedObjective.value) {
    tmpObjective.value = "";
  }

  const loading = showLoadingToast(
    t(
      tmpObjective.value
        ? `Message.difficultyLevelGeneration`
        : `Message.targetGeneration`
    )
  );

  const res = await ApiPostGenerateObjective({
    mateId: mateId!,
    missionId: mission!.id,
    locationId: location!.id,
    objective: tmpObjective.value,
    difficultyLevel: tmpDifficultyLevel.value ?? DispatchDifficultyLevel.Easy,
    memberIdzz: dispatchMemberzz?.map((v) => v.mate!.id),
    carriedJournalIdzz: carriedJournalzz?.map((v) => v.id),
    carriedMailIdzz: carriedMailzz?.map((v) => v.id),
    carriedDispatchIdzz: carriedDispatchzz?.map((v) => v.id),
  });
  if (typeof res !== "string" && res.data) {
    const { goalContent, difficultyLevel, reason, signature } = res.data;
    tmpObjective.value = goalContent;
    tmpDifficultyLevel.value = difficultyLevel as DispatchDifficultyLevel;
    tmpReason.value = reason;
    tmpSignature.value = signature;
    returnedObjective.value = goalContent;
  }

  userStore.refresh();
  loading.close();
};

const showDifficultyLevelModal = ref(false);

const difficultyLevelOptions = computed(() => {
  return Object.values(DispatchDifficultyLevel)
    .filter((v) => Number.isInteger(v))
    .map((v) => ({
      text: t(`mission.difficultyLevel.${v}`),
      value: v,
    }));
});

const clickDifficultyLevel = async (value: DispatchDifficultyLevel) => {
  if (tmpObjective.value) {
    try {
      await showConfirmDialog({
        title: "确定要修改任务难度吗？",
        message:
          "修改后，当前任务目标将会被清空并根据任务难度重新生成任务目标(消耗 1 能量块)",
        confirmButtonText: "确定",
        cancelButtonText: "取消",
      });
    } catch {
      return;
    }
  }

  tmpDifficultyLevel.value = value;

  if (tmpObjective.value) {
    tmpObjective.value = "";
    await clickGenerateObjective();
  }

  showDifficultyLevelModal.value = false;
};

const clickSelectDifficultyLevel = () => {
  showDifficultyLevelModal.value = true;
};

const clickSave = async () => {
  const { dispatchMission } = props;
  if (tmpObjective.value !== returnedObjective.value) {
    try {
      await showConfirmDialog({
        message: `目标内容已变更，是否重新评估难度？${getCostText(1)}`,
        confirmButtonText: "确定",
        cancelButtonText: "取消",
      });
      clickGenerateObjective();
    } catch {
      console.log("cancel");
    }
  }

  dispatchMission.objective = tmpObjective.value;
  dispatchMission.difficultyLevel = tmpDifficultyLevel.value;
  dispatchMission.difficultyLevelReason = tmpReason.value;
  dispatchMission.objectiveSignature = tmpSignature.value;
  emit("update:show", false);
};

const getCostText = (amount: number) => {
  if (userStore.isFreeModel()) return "";
  return `（消耗 ${userStore.getPowerCostByModel(amount)} 能量块）`;
};
</script>
